import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import {
    CloseTwoTone,
    MenuTwoTone,
    WbSunnyRounded,
    NightsStayRounded,
    FavoriteBorderRounded,
    ShoppingCartRounded,
} from '@mui/icons-material';
import { Tooltip, Box, IconButton, Badge, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OptionsMenu from './Optionsmenu';
import { useThemeMode } from '../../context/ThemeModeContext';
import { useEcomType } from '../../context/EcomTypeContext';
import { PROD_ECOM_TYPE } from '../../constant/constant';
import BreadcrumbsPath from './BreadcrumbsPath';
import { useNavigate } from 'react-router-dom';
import { useGetDashboardCounts } from '../../apis/dashboardAPI/DashboardAPI';

export default function DesktopHeader({ onToggleSidebar, sidebarOpen }) {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mode, toggleThemeMode } = useThemeMode();
    const { prodEcomType, toggleEcomType } = useEcomType();
    const { data: counts } = useGetDashboardCounts();
    const cartCount = counts?.cartCount ?? 0;
    const wishlistCount = counts?.wishlistCount ?? 0;

    return (
        <Box
            sx={{
                display: { xs: 'none', md: 'block' },
                width: '100%',
                position: 'sticky',
                top: 0,
                p: 0.5,
                zIndex: 1100,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                backdropFilter: 'blur(12px)',
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 0% 0%, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 50%)`,
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
                    // animation: `${shimmer} 3s infinite`,
                    pointerEvents: 'none',
                },
            }}
        >
            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1.5,
                    maxWidth: '1700px',
                    mx: 'auto',
                    position: 'relative',
                    zIndex: 1,
                }}
                spacing={2}
            >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={onToggleSidebar}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: theme.shape.borderRadius,
                            background: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: 0,
                                height: 0,
                                borderRadius: '50%',
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                transform: 'translate(-50%, -50%)',
                                transition: 'width 0.6s, height 0.6s',
                            },
                            '&:hover': {
                                background: alpha(theme.palette.primary.main, 0.15),
                                transform: 'scale(1.05)',
                                '&::before': {
                                    width: 100,
                                    height: 100,
                                },
                            },
                        }}
                    >
                        {sidebarOpen ? <CloseTwoTone /> : <MenuTwoTone />}
                    </IconButton>
                    <BreadcrumbsPath />
                </Stack>

                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ fontWeight: prodEcomType === PROD_ECOM_TYPE.ESSENTIAL ? 700 : 400, color: prodEcomType === PROD_ECOM_TYPE.ESSENTIAL ? 'primary.main' : 'text.secondary', transition: 'all 0.2s' }}>
                            Essential
                        </Typography>
                        <Switch
                            checked={prodEcomType === PROD_ECOM_TYPE.LIFESTYLE}
                            onChange={toggleEcomType}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: 'secondary.main' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'secondary.main' },
                            }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: prodEcomType === PROD_ECOM_TYPE.LIFESTYLE ? 700 : 400, color: prodEcomType === PROD_ECOM_TYPE.LIFESTYLE ? 'secondary.main' : 'text.secondary', transition: 'all 0.2s' }}>
                            Lifestyle
                        </Typography>
                    </Stack>
                    <>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Tooltip title={t('nav.wishlist')}>
                                <IconButton size="small" onClick={() => navigate('/wishlist')}>
                                <Badge badgeContent={wishlistCount} color="error" invisible={wishlistCount === 0} sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
                                        <FavoriteBorderRounded sx={{ fontSize: 20 }} />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Tooltip title={t('nav.cart')}>
                            <IconButton size="small" onClick={() => navigate('/cart')}>
                                <Badge badgeContent={cartCount} color="error" invisible={cartCount === 0} sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
                                    <ShoppingCartRounded sx={{ fontSize: 20 }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    </>

                    <Tooltip title={mode === 'dark' ? t('header.lightMode') : t('header.darkMode')} arrow>
                        <IconButton
                            onClick={toggleThemeMode}
                            size="small"
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: theme.shape.borderRadius,
                                background: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: alpha(theme.palette.primary.main, 0.15),
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            {mode === 'dark' ? <WbSunnyRounded sx={{ fontSize: 16 }} /> : <NightsStayRounded sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Tooltip>
                    <OptionsMenu />
                </Stack>
            </Stack>

            {/* Subtle bottom accent line */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.5)}, transparent)`,
                }}
            />
        </Box>
    );
}
